import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  ConfirmationDialog,
  DatePicker,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  filterToolbarControlSmClassName,
} from '@roger/ui-components';
import { FileSpreadsheet, History, Loader2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import {
  deletePayrollEntry,
  exportAllPayrollEntries,
  exportPayrollEntryVisits,
  listPayrollEntries,
} from '../../services/api';
import type { PayrollEntryItem } from '../../services/api/types.gen';
import { PaginationBar } from './PaginationBar';
import { useToast } from '../../hooks/useToastContext';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';

/** Payrolls already created — one row per payroll date, drilling into its visits. */
export const PaidPayrollTab: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const hideImportedPatients = useStore(
    state => state.payrollFilters.hideImportedPatients !== false
  );
  const setPayrollFilters = useStore(state => state.setPayrollFilters);
  const pageSize = useStore(state => state.payrollPageSizes.paid);
  const setPayrollPageSize = useStore(state => state.setPayrollPageSize);

  const [entries, setEntries] = useState<PayrollEntryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<PayrollEntryItem | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  // The id currently exporting, so only that row's icon spins. A boolean would
  // spin every row at once and say nothing about which file is coming.
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [exportFrom, setExportFrom] = useState<string | null>(null);
  const [exportTo, setExportTo] = useState<string | null>(null);
  const rangeIsBackwards = !!exportFrom && !!exportTo && exportFrom > exportTo;

  useEffect(() => {
    setPage(1);
  }, [hideImportedPatients]);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = {
        page,
        per_page: pageSize,
        hide_imported_patients: hideImportedPatients,
      };
      const response = await listPayrollEntries({
        query,
        throwOnError: true,
      });
      if (response.data) {
        setEntries(response.data.items);
        setTotal(response.data.total);
        setTotalPages(response.data.total_pages);
      }
    } catch (error) {
      console.error('Failed to load payrolls:', error);
      toast.error(
        getBusinessErrorMessage(error) ??
          'Failed to load payrolls. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
    // toast is intentionally omitted: useToast returns a fresh object every
    // render, so including it would refetch in a loop (OASISManager pattern).
  }, [hideImportedPatients, page, pageSize]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  /** Shared tail of both exports: surface the server's reason, or download. */
  const runExport = async (
    request: Promise<{ error?: unknown; data?: unknown }>,
    fallbackFilename: string
  ): Promise<void> => {
    try {
      const response = await request;
      if (response.error) {
        console.error('Failed to export payroll:', response.error);
        toast.error(
          await getUserFacingApiMessageAsync(
            response,
            'Failed to export. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(
        response as Parameters<typeof downloadResponseAsFile>[0],
        fallbackFilename
      );
    } catch (error) {
      console.error('Failed to export payroll:', error);
      toast.error(
        (await getUserFacingApiMessageAsync(
          error,
          'Failed to export. Please try again.'
        )) ?? 'Failed to export. Please try again.'
      );
    }
  };

  // Both handlers clear their flag in a finally. runExport swallows its own
  // errors today, so nothing can escape, but that flag is what keeps every
  // export button on the screen disabled: if one ever did escape, the whole
  // table would be stranded mid-spin with no way back except a reload.
  const handleExportEntry = async (entry: PayrollEntryItem) => {
    setExportingId(entry.id);
    try {
      await runExport(
        exportPayrollEntryVisits({
          path: { payroll_entry_id: entry.id },
          query: { hide_imported_patients: false },
          responseType: 'blob',
        }),
        `RogerPayroll_${entry.payroll_date}.xlsx`
      );
    } finally {
      setExportingId(null);
    }
  };

  const handleExportAll = async () => {
    if (rangeIsBackwards) return;
    setIsExportingAll(true);
    try {
      await runExport(
        exportAllPayrollEntries({
          query: {
            start_date: exportFrom ?? undefined,
            end_date: exportTo ?? undefined,
            // Paid payrolls are sealed financial records; exports include every visit.
            hide_imported_patients: false,
          },
          responseType: 'blob',
        }),
        'RogerPayrolls.xlsx'
      );
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    setIsDeleting(true);
    try {
      const response = await deletePayrollEntry({
        path: { payroll_entry_id: entryToDelete.id },
        throwOnError: true,
      });
      toast.success(
        `Payroll deleted. ${response.data.released_count} visits released.`
      );
      setEntryToDelete(null);
      // Step back a page when the delete emptied the current one.
      if (entries.length === 1 && page > 1) {
        setPage(p => p - 1);
      } else {
        await fetchEntries();
      }
    } catch (error) {
      console.error('Failed to delete payroll:', error);
      toast.error(
        getBusinessErrorMessage(error) ??
          'Failed to delete payroll. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex h-8 items-center gap-2'>
        <Checkbox
          id='paid-hide-imported-patients'
          checkboxSize='sm'
          checked={hideImportedPatients}
          onCheckedChange={checked => {
            setPayrollFilters({ hideImportedPatients: checked === true });
            setPage(1);
          }}
        />
        <label
          htmlFor='paid-hide-imported-patients'
          className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
        >
          Hide imported patients
        </label>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-3 text-muted-foreground'>
            Loading payrolls...
          </span>
        </div>
      ) : entries.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <History className='h-12 w-12 mx-auto mb-4 text-gray-500' />
          <p>No payrolls created yet</p>
          <p className='text-sm'>Create one from the Unpaid tab</p>
        </div>
      ) : (
        <div className='space-y-4'>
          <div className='flex flex-wrap items-end justify-end gap-3'>
            <div className={filterToolbarControlSmClassName}>
              <FormLabel htmlFor='exportFrom' showTooltip={false}>
                Payrolls From
              </FormLabel>
              <DatePicker
                id='exportFrom'
                className='w-full'
                typable
                showClearButton
                date={exportFrom ? parseISOToLocalDate(exportFrom) : undefined}
                onDateChange={d => setExportFrom(d ? formatDateToISO(d) : null)}
              />
            </div>

            <div className={filterToolbarControlSmClassName}>
              <FormLabel htmlFor='exportTo' showTooltip={false}>
                Payrolls To
              </FormLabel>
              <DatePicker
                id='exportTo'
                className='w-full'
                typable
                showClearButton
                date={exportTo ? parseISOToLocalDate(exportTo) : undefined}
                onDateChange={d => setExportTo(d ? formatDateToISO(d) : null)}
              />
            </div>

            <Button
              variant='outline'
              size='sm'
              onClick={handleExportAll}
              disabled={isExportingAll || !!exportingId || rangeIsBackwards}
              className='h-8 self-end'
            >
              {isExportingAll ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <FileSpreadsheet className='h-4 w-4' />
              )}
              Export to Excel
            </Button>
          </div>

          {rangeIsBackwards && (
            <p className='text-sm text-destructive text-right'>
              The From date must not be after the To date.
            </p>
          )}

          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payroll Date</TableHead>
                  <TableHead>Clinic</TableHead>
                  <TableHead className='text-right'>Visits</TableHead>
                  <TableHead className='w-20 text-center'>Export</TableHead>
                  <TableHead className='w-20 text-center'>Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => (
                  <TableRow
                    key={entry.id}
                    className='cursor-pointer'
                    onClick={() => navigate(`/payroll-manager/${entry.id}`)}
                  >
                    <TableCell className='text-sm font-medium'>
                      {formatDateToMMDDYYYY(entry.payroll_date)}
                    </TableCell>
                    <TableCell className='text-sm'>
                      {entry.clinic_name}
                    </TableCell>
                    <TableCell className='text-right text-sm'>
                      {entry.visit_count}
                    </TableCell>
                    <TableCell className='w-20 text-center'>
                      <Button
                        size='sm'
                        variant='ghost'
                        aria-label={`Export payroll ${formatDateToMMDDYYYY(entry.payroll_date)} to Excel`}
                        // stopPropagation because the row itself navigates to
                        // the payroll: without it the download starts and the
                        // screen changes underneath it.
                        onClick={e => {
                          e.stopPropagation();
                          handleExportEntry(entry);
                        }}
                        disabled={
                          !!exportingId ||
                          isExportingAll ||
                          entry.visit_count === 0
                        }
                      >
                        {exportingId === entry.id ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <FileSpreadsheet className='h-4 w-4' />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className='w-20 text-center'>
                      <Button
                        size='sm'
                        variant='ghost'
                        aria-label='Delete payroll'
                        onClick={e => {
                          e.stopPropagation();
                          setEntryToDelete(entry);
                        }}
                      >
                        <Trash2 className='h-4 w-4 text-destructive' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            itemsLabel='payrolls'
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPayrollPageSize('paid', size);
              setPage(1);
            }}
          />
        </div>
      )}

      <ConfirmationDialog
        open={!!entryToDelete}
        title='Delete payroll?'
        message={
          entryToDelete
            ? `This deletes the ${formatDateToMMDDYYYY(entryToDelete.payroll_date)} ${entryToDelete.clinic_name} payroll and releases its ${entryToDelete.visit_count} visits. Visits in a status your agency pays for go back to Unpaid.`
            : ''
        }
        confirmLabel='Delete'
        loading={isDeleting}
        onAccept={handleDelete}
        onCancel={() => setEntryToDelete(null)}
      />
    </div>
  );
};
